import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBackTarget } from '../../hooks/useBackTarget';

const font = "'Inria Serif', serif";
const SUPPORT_EMAIL = 'contact@allurexp.com';
const LAST_UPDATED = 'February 12, 2026';

const COMPANY = {
  name: 'ALLURE EXPERIENCE I.K.E.',
  nameLatin: 'ALLURE EXPERIENCE P.C.',
  distinctive: 'ALLUREXP',
  vat: 'EL803335274',
  gemi: '194817609000',
  addressLatin: 'Emmanouil Dimela 74, 16674 Athens, Greece',
  phone: '+30 694 396 3291',
};

const sectionStyle = { marginTop: 28 };
const h2Style = {
  fontFamily: font,
  fontSize: 22,
  fontWeight: 700,
  color: '#111',
  margin: '0 0 10px',
};
const pStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.6,
  color: '#333',
  margin: '0 0 10px',
};
const listStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.7,
  color: '#333',
  paddingLeft: 22,
  margin: '0 0 10px',
};

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="terms-of-service-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        padding: '20px 20px 80px',
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <button
        type="button"
        onClick={goBack}
        data-testid="terms-back-button"
        aria-label="Back"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: 'transparent',
          fontFamily: font,
          fontSize: 15,
          color: '#703FF6',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 14,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1
        style={{
          fontFamily: font,
          fontSize: 30,
          fontWeight: 700,
          color: '#111',
          margin: 0,
        }}
      >
        Allure — Terms of Service
      </h1>
      <p style={{ ...pStyle, color: '#666', marginTop: 8 }}>
        Last updated: {LAST_UPDATED}
      </p>

      <section style={sectionStyle}>
        <p style={pStyle}>
          These Terms of Service ("Terms") govern your access to and use of the Allure mobile
          application, web app, and related services (together, the "Service"), operated by{' '}
          <strong>{COMPANY.nameLatin}</strong> (Greek legal name: <em>{COMPANY.name}</em>, distinctive title "{COMPANY.distinctive}"), a private company (IKE) incorporated in Greece, with registered seat at {COMPANY.addressLatin}, Γ.Ε.ΜΗ. {COMPANY.gemi} and VAT/ΑΦΜ {COMPANY.vat} (referred to below as "Allure", "we", "us", "our").
          By creating an account or using the Service you agree to these Terms.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>1. Accounts and eligibility</h2>
        <p style={pStyle}>
          You must be at least 16 years old to create an Allure account. You are responsible
          for maintaining the confidentiality of your credentials and for all activity under
          your account.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>1a. Operational messages from Allure Support</h2>
        <p style={pStyle}>
          Messages sent to you by the Allure Support team (including replies to your tickets,
          account, safety and legal notifications) are considered operational communications and
          are always delivered — they bypass your in-app notification preferences and messaging
          privacy toggles. The only way to stop them is to disable Allure notifications entirely
          at the operating system level (or to never grant them in the first place). By using
          the Service you acknowledge that these messages are part of the service itself.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>2. The Service</h2>
        <p style={pStyle}>Allure offers three complementary experiences:</p>
        <ul style={listStyle}>
          <li>
            <strong>Guest:</strong> receive a QR pass, enjoy automatic discounts at partner
            venues, and optionally tip hosts and venue staff.
          </li>
          <li>
            <strong>Host:</strong> invite guests using the Allure pass, earn commission on their
            qualifying partner-venue spend, and receive voluntary guest tips.
          </li>
          <li>
            <strong>Venue Owner / Staff:</strong> accept payments via Allure, track analytics,
            and receive payouts.
          </li>
        </ul>
        <p style={pStyle}>
          <strong>Allure is a technology marketplace and intermediary only.</strong> We suggest
          independent hospitality venues to guests based on partnership. We do not operate,
          manage, employ staff at, or otherwise control any partner venue. Our role is to
          facilitate the connection between guests and venues — not to act as the venue, as a
          travel agent, or as a guarantor of the guest experience.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>2a. Venue independence and guest safety</h2>
        <p style={pStyle}>
          Every partner venue is an independent business operating under its own licences,
          management, and legal obligations. Once you arrive at a venue, the venue is solely
          and exclusively responsible for:
        </p>
        <ul style={listStyle}>
          <li>
            The quality, preparation, and safety of all food and beverages served, including
            the management of allergens, dietary restrictions, and intolerances.
          </li>
          <li>
            Any illness, allergic reaction, food poisoning, injury, or adverse health event
            suffered on the venue's premises or as a result of consuming the venue's products.
          </li>
          <li>
            Compliance with all applicable food-safety, health, hygiene, fire-safety, licensing,
            and consumer-protection laws in force at the venue's location.
          </li>
          <li>
            The overall guest experience, staff conduct, and conditions at the venue.
          </li>
        </ul>
        <p style={pStyle}>
          <strong>Allure accepts no liability whatsoever for any of the above.</strong> If you
          have a health condition, allergy, or dietary restriction, you must communicate this
          directly to the venue before or upon arrival — Allure does not relay guest health
          information to venues and has no obligation to do so. By using the Service you
          acknowledge that the contractual relationship for the hospitality service is between
          you and the venue, not between you and Allure.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>3. Payments and fees</h2>
        <p style={pStyle}>
          Guest payments are processed by our licensed payment institution Viva Payment Services
          S.A. ("Viva Wallet"). Viva Wallet splits every authorised transaction in real time —
          Allure never takes custody of the venue's share of any transaction. The Allure
          marketplace fee is disclosed to guests at checkout and to venues in the venue dashboard.
        </p>
        <p style={pStyle}>
          Venues receive the net amount (bill minus the Allure marketplace fee, minus guest tips
          left to staff, minus Viva Wallet card-processing fees). Where a venue connects a plain
          IBAN (non-Viva) as its payout destination, an additional €0.80 per transaction is
          retained by Allure to cover the SEPA payout cost incurred by Viva Wallet; this is
          disclosed to the venue on the payout-connection screen.
        </p>
        <p style={pStyle}>
          Hosts earn a referral commission on qualifying guest spend at partner venues, plus any
          voluntary guest tips, plus an optional share of the monthly host pool if they qualify
          under the rules published in the host dashboard. Host payouts are made monthly to the
          host's own IBAN. Hosts must provide a valid tax identifier (ΑΦΜ / TIN) before the first
          payout is released — see Section 4 of the Host Listing Agreement.
        </p>
        <p style={pStyle}>
          All amounts are in Euros unless stated otherwise. Allure does not guarantee the
          availability or quality of any partner venue; your relationship with the venue is
          governed by the venue’s own terms.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>4. Refunds</h2>
        <p style={pStyle}>
          <strong>Allure does not process refunds and is not able to do so.</strong> Here is
          why: every guest payment is split in real time by our licensed payment processor
          (Viva Wallet) at the moment of authorisation. The venue's share is routed directly
          and immediately to the venue's own bank account or Viva Wallet merchant account.
          Allure never takes custody of those funds. Because the money goes directly to the
          venue, only the venue can issue a refund.
        </p>
        <p style={pStyle}>
          If you are unhappy with a transaction — whether due to poor service, a product
          issue, an illness, or any other reason arising from the venue's operations —
          you must raise the refund request directly with the venue. Allure can, at its
          discretion, facilitate communication between you and the venue through the
          in-app support channel, but this does not create any obligation on Allure to
          compensate you or to intervene in the venue's commercial decisions.
        </p>
        <p style={pStyle}>
          If you believe a transaction is fraudulent (i.e. you did not make the payment
          at all), contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>
            {SUPPORT_EMAIL}
          </a>{' '}
          within 30 days and we will investigate with Viva Wallet.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>5. Acceptable use</h2>
        <ul style={listStyle}>
          <li>No fraudulent, abusive, or illegal behaviour.</li>
          <li>No scraping, reverse-engineering, or interfering with the Service.</li>
          <li>No impersonation of other users, venues, hosts, or Allure staff.</li>
          <li>No misuse of QR passes (sharing passes for fraudulent discounts).</li>
        </ul>
        <p style={pStyle}>
          We may suspend or terminate accounts that violate these rules, with or without notice.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>6. Intellectual property</h2>
        <p style={pStyle}>
          Allure, the Allure logo, and all Service content are owned by {COMPANY.nameLatin}
          and protected by applicable trademark and copyright laws. You retain ownership of any
          content (reviews, photos, messages) you submit, but you grant Allure a worldwide,
          royalty-free licence to display and distribute that content within the Service.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>7. Disclaimer and limitation of liability</h2>
        <p style={pStyle}>
          The Service is provided "as is" without warranties of any kind. Allure makes no
          representation that any partner venue meets any particular standard of quality,
          safety, hygiene, or suitability for your specific needs.
        </p>
        <p style={pStyle}>
          <strong>Allure expressly disclaims all liability for:</strong>
        </p>
        <ul style={listStyle}>
          <li>
            Any personal injury, illness, allergic reaction, food poisoning, or adverse health
            event arising from your visit to, or consumption of products at, a partner venue.
          </li>
          <li>
            Any loss, theft, damage to property, or accident that occurs on a venue's premises.
          </li>
          <li>
            The accuracy, completeness, or fitness for purpose of any venue's menu, allergen
            information, or product descriptions.
          </li>
          <li>
            Any failure by a venue to honour an Allure pass, apply the correct discount, or
            deliver the expected level of service.
          </li>
          <li>
            Any act or omission of a venue's staff, management, or third-party suppliers.
          </li>
        </ul>
        <p style={pStyle}>
          To the maximum extent permitted by applicable law, Allure's aggregate liability for
          any claim arising out of or relating to these Terms — including claims connected to
          a venue visit — shall not exceed the greater of (a) €100 or (b) the total fees you
          paid to Allure in the 12 months preceding the claim. This cap applies regardless of
          the legal theory on which the claim is based (contract, tort, statute, or otherwise).
        </p>
        <p style={pStyle}>
          Nothing in this section limits liability that cannot be excluded under applicable
          mandatory law (including statutory consumer rights in your country of residence).
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>8. Governing law</h2>
        <p style={pStyle}>
          These Terms are governed by the laws of Greece. The competent courts of Athens, Greece,
          have exclusive jurisdiction over any dispute, without prejudice to mandatory consumer-
          protection rules that may grant you additional rights in your country of residence.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>9. Changes</h2>
        <p style={pStyle}>
          We may update these Terms. Material changes will be announced in the app at least 14
          days before they take effect.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>10. Contact</h2>
        <p style={pStyle}>
          <strong>{COMPANY.nameLatin}</strong><br />
          {COMPANY.addressLatin}<br />
          Γ.Ε.ΜΗ. {COMPANY.gemi} — ΑΦΜ {COMPANY.vat}<br />
          Phone: {COMPANY.phone}<br />
          Email:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>
            {SUPPORT_EMAIL}
          </a>
          <br />
          See also our{' '}
          <Link to="/privacy" style={{ color: '#703FF6' }} data-testid="terms-privacy-link">
            Privacy Policy
          </Link>
          {' '}and our{' '}
          <Link to="/legal/imprint" style={{ color: '#703FF6' }} data-testid="terms-imprint-link">
            Legal / Imprint
          </Link>
          {' '}page.
        </p>
      </section>
    </div>
  );
}
